import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../styles/about.css"; // Neural theme stylesheet
import ohioTractor from "../assets/images/ohioTractor.jpeg";
import { ChevronRight, Code, BarChart2, Zap, Briefcase, GitMerge, Server, Users, Lightbulb, Brain, Network } from "lucide-react";

const About = () => {
  const navigate = useNavigate();
  const [neuralNodes, setNeuralNodes] = useState([]);
  
  useEffect(() => {
    // Generate neural network nodes for background animation
    const nodes = Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 3
    }));
    setNeuralNodes(nodes);
    
    // Add schema markup for About page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Colee Software Services LLC",
      "foundingDate": "2025",
      "description": "Innovative technology solutions driving business success through strategic implementation and expert guidance.",
      "serviceType": ["Custom Software Development", "Technology Consulting", "Digital Innovation"]
    });
    document.head.appendChild(script);
    
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  return (
    <section className="about-section min-vh-100">
      {/* Neural Network Background Animation */}
      <div className="neural-background">
        <div className="quantum-grid"></div>
        {neuralNodes.map(node => (
          <div
            key={node.id}
            className="neural-node"
            style={{
              left: `${node.x}%`,
              top: `${node.y}%`,
              animation: `neuralPulse 3s ease-in-out infinite ${node.delay}s`
            }}
          />
        ))}
        <svg className="neural-connections" width="100%" height="100%">
          {neuralNodes.slice(0, 4).map((node, i) => {
            const nextNode = neuralNodes[(i + 1) % 4];
            return (
              <line
                key={i}
                className="neural-line"
                x1={`${node.x}%`}
                y1={`${node.y}%`}
                x2={`${nextNode.x}%`}
                y2={`${nextNode.y}%`}
              />
            );
          })}
        </svg>
      </div>

      {/* Background with parallax effect */}
      <div className="about-background-wrapper">
        <div 
          className="about-background-image" 
          style={{ backgroundImage: `url(${ohioTractor})` }} 
        />
        <div className="about-overlay" />
      </div>

      <div className="container position-relative py-5">
        {/* Header Section with animated reveal */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-10 text-center about-header-content">
            <div className="about-badge mb-3">ESTABLISHED 2025</div>
            <h1 className="about-main-title display-4 text-white mb-4">
              About <span className="about-highlight">Colee Software Services</span>
            </h1>
            <div className="about-title-accent mx-auto" />
            <p className="about-lead-text lead mt-4">
              We deliver <span className="text-gradient-cyan">Tech Solutions</span> that drive business success. 
              Through strategic implementation and expert guidance, we transform complex 
              technical challenges into competitive advantages for businesses of all sizes.
            </p>
            <div className="about-cta-buttons mt-5">
              <button 
                className="btn btn-primary btn-lg about-primary-btn me-3"
                onClick={() => navigate('/industries')}
              >
                Our Work <ChevronRight size={18} className="ms-1" />
              </button>
              <button 
                className="btn btn-outline-light btn-lg about-secondary-btn"
                onClick={() => navigate('/contact')}
              >
                Contact Us
              </button>
            </div>
            {/* Neural Status Panel */}
            <div className="neural-status mt-5">
              <h3>Network Status</h3>
              <div className="status-grid">
                <div className="status-item">
                  <div className="status-value">99.5%</div>
                  <div className="status-label">Uptime</div>
                </div>
                <div className="status-item">
                  <div className="status-value">100%</div>
                  <div className="status-label">Sale Driving Metrics</div>
                </div>
                <div className="status-item">
                  <div className="status-value">97.3%</div>
                  <div className="status-label">Network Analytics</div>
                </div>
                <div className="status-item">
                  <div className="status-value">ACTIVE</div>
                  <div className="status-label">Quantitative outcomes</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Core Capabilities Section */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-10">
            <div className="about-section-header text-center mb-4">
              <h2 className="about-section-title text-white">Core Capabilities</h2>
              <div className="about-section-accent mx-auto"></div>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="row g-4 mb-5">
          {features.map((feature, index) => (
            <div key={index} className="col-md-4">
              <div className="about-feature-card h-100">
                <div className="card-body p-4">
                  <div className="about-feature-icon mb-3">{feature.icon}</div>
                  <h3 className="about-feature-title h4 mb-3">{feature.title}</h3>
                  <p className="about-feature-text mb-0">{feature.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Solutions Portfolio Section */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-10">
            <div className="about-section-header text-center mb-4">
              <h2 className="about-section-title text-white">Solutions Portfolio</h2>
              <div className="about-section-accent mx-auto"></div>
            </div>
          </div>
        </div>

        {/* Solutions Grid */}
        <div className="row">
          <div className="col-12">
            <div className="about-solutions-container rounded-3 p-4 p-md-5">
              <div className="row g-4">
                {solutions.map((solution, index) => (
                  <div key={index} className="col-md-6 col-lg-4">
                    <div className="about-solution-card p-4 rounded-3 h-100">
                      <div className="about-solution-icon mb-3">{solution.icon}</div>
                      <div className="about-solution-number mb-2">
                        {String(index + 1).padStart(2, '0')}
                      </div>
                      <h4 className="about-solution-title h5 mb-3">{solution.title}</h4>
                      <p className="about-solution-text mb-0">{solution.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Expertise Section */}
        <div className="row justify-content-center mt-5 mb-4">
          <div className="col-lg-10">
            <div className="about-section-header text-center mb-4">
              <h2 className="about-section-title text-white">Our Expertise</h2>
              <div className="about-section-accent mx-auto"></div>
            </div>
            <div className="about-expertise-container p-4 p-md-5 rounded-3">
              <div className="row g-4">
                {expertise.map((item, index) => (
                  <div key={index} className="col-md-6">
                    <div className="about-expertise-item d-flex align-items-center mb-3">
                      <div className="about-expertise-marker me-3"></div>
                      <h5 className="about-expertise-title mb-0">{item}</h5>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* CTA Footer with Neural Energy */}
        <div className="row justify-content-center mt-5">
          <div className="col-lg-8 text-center">
            <h2 className="cta-title display-4 fw-bold mb-4">
              Innovate with Colee Software 
            </h2>
            <p className="cta-description fs-5 mb-5 mx-auto">
              Partner with us to harness cutting-edge technology and revolutionize your business.
            </p>
            {/* <button 
              className="cta-button"
              onClick={() => navigate('/contact')}
            >
              Initiate Neural Link
              <span className="cta-icon">⚡</span>
            </button> */}
          </div>
        </div>
      </div>
    </section>
  );
};

const features = [
  {
    icon: <Zap className="about-icon" />,
    title: "Technical Excellence",
    description: "Leveraging cutting-edge technologies and proven methodologies to deliver scalable, efficient solutions that exceed industry standards."
  },
  {
    icon: <BarChart2 className="about-icon" />,
    title: "Strategic Solutions",
    description: "Providing comprehensive digital transformation services and innovative software solutions across multiple industries and technologies."
  },
  {
    icon: <Lightbulb className="about-icon" />,
    title: "Innovation Focus",
    description: "Creating accessible and powerful software solutions that bridge the gap between complex technology and practical business requirements."
  }
];

const solutions = [
  {
    icon: <Code className="about-solution-icon" />,
    title: "Custom Software Development",
    description: "Enterprise-grade software solutions tailored to specific business requirements and growth objectives."
  },
  {
    icon: <Briefcase className="about-solution-icon" />,
    title: "Technology Consulting",
    description: "Expert guidance for optimal technology implementation and strategic digital transformation."
  },
  {
    icon: <GitMerge className="about-solution-icon" />,
    title: "System Integration",
    description: "Seamless integration of modern solutions with existing systems for improved efficiency."
  },
  {
    icon: <Users className="about-solution-icon" />,
    title: "Project Leadership",
    description: "Efficient delivery of complex technical projects with measurable results and clear ROI."
  },
  {
    icon: <Server className="about-solution-icon" />,
    title: "Cloud Architecture",
    description: "Scalable cloud infrastructure design and implementation for modern business needs."
  },
  {
    icon: <Brain className="about-solution-icon" />,
    title: "Digital Innovation",
    description: "Forward-thinking solutions that drive competitive advantage and business growth with neural intelligence."
  }
];

const expertise = [
  "React & Modern Frontend Development",
  "Enterprise Software Architecture",
  "Cloud Infrastructure & DevOps",
  "Data Analytics & Business Intelligence",
  "Mobile Application Development",
  "API Design & Integration",
  "UI/UX Design & Implementation",
  "Security & Compliance Solutions"
];

export default About;